// FeedbackWidget.jsx
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext.jsx";
import { submitFeedback } from "../api.js"; // adjust path as needed

export default function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const acRef = useRef(null);

  useEffect(() => () => acRef.current?.abort?.(), []);
  useEffect(() => {
    if (!open) setEmail(user?.email || "");
  }, [user, open]);

  const openPanel = () => {
    setError("");
    setOk(false);
    setMessage("");
    setEmail(user?.email || "");
    setOpen(true);
  };
  const closePanel = () => setOpen(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setOk(false);
    const trimmedMsg = message.trim();
    const trimmedEmail = email.trim();

    if (!trimmedMsg || trimmedMsg.length < 3)
      return setError("Please include a brief message (at least 3 characters).");
    if (!trimmedEmail || !/\S+@\S+\.\S+/.test(trimmedEmail))
      return setError("Please enter a valid email address.");

    acRef.current?.abort?.();
    const ac = new AbortController();
    acRef.current = ac;
    setSubmitting(true);
    try {
      await submitFeedback({ email: trimmedEmail, message: trimmedMsg, signal: ac.signal });
      setOk(true);
    } catch (err) {
      setError(err.message || "Failed to send feedback");
    } finally {
      setSubmitting(false);
    }
  };

  // close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && closePanel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* Floating Feedback button */}
      <button
        className="feedback-fab btn btn-primary"
        aria-label="Send feedback"
        onClick={openPanel}
      >
        Feedback
      </button>

      {open && (
        <div
          className="feedback-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-title"
        >
          <div className="feedback-header">
            <h4 id="feedback-title">Send feedback</h4>
            <button
              className="feedback-close"
              aria-label="Close"
              onClick={closePanel}
              title="Close"
            >
              ✕
            </button>
          </div>

          {ok ? (
            <div className="feedback-success">Thanks! Your feedback was sent.</div>
          ) : (
            <form onSubmit={onSubmit} className="form" noValidate>
              <label className="field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              <label className="field" style={{ marginTop: 6 }}>
                <span>Message</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Spot and error? Are we missing something?"
                  rows={4}
                  required
                  style={{
                    width: "100%",
                    resize: "vertical",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                />
              </label>

              {error && <div className="error" aria-live="assertive">{error}</div>}

              <div className="form-actions" style={{ marginTop: 10 }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
